import { useCallback, useEffect, useState } from 'react';
import {
  Table, Input, Button, Space, Modal, Form, Switch, App, Tag, Popconfirm,
  AutoComplete, Select, Tooltip, Typography,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  MinusCircleOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Rule } from 'antd/es/form';
import dayjs from 'dayjs';
import {
  listTaskTypesForAdmin,
  createTaskType,
  updateTaskType,
  deleteTaskType,
  TaskTypeDetail,
  DagTemplateNameMap,
  KNOWN_BUILD_TYPES,
  type SimType,
} from '@apis/simulation';

type EditorMode = 'create' | 'edit';

interface EditorState {
  open: boolean;
  mode: EditorMode;
  record?: TaskTypeDetail;
}

/** 表单内 KV 行的中间表示；提交前再转回 Record<string,string>。 */
interface DagTemplateEntry {
  /** ``default`` 行 key 锁定为字符串 'default'，由 UI 禁用 key 输入框保证 */
  key: string;
  value: string;
}

interface FormValues {
  name: string;
  taskTypeCode?: string;
  /** 第一行固定为 default，其余为可选附加映射 */
  dagTemplateEntries: DagTemplateEntry[];
  dispatchProjectId?: string;
  /** 前端使用布尔值呈现「是否显示」，提交时映射为 enableFlag: 1/0 */
  visible: boolean;
  /** 归属仿真类型，默认 'planning' */
  simType: SimType;
  /** 描述,可空 */
  description?: string;
}

const DAG_TEMPLATE_DEFAULT_KEY = 'default';

/** 归属仿真类型的展示配置：文案 + Tag 颜色 + 表单顺序 */
const SIM_TYPE_OPTIONS: {
  value: SimType;
  label: string;
  color: string;
  description: string;
}[] = [
  { value: 'planning', label: 'PNC仿真', color: 'geekblue', description: '规控（PNC）主链路，默认' },
  { value: 'perception', label: '感知仿真', color: 'green', description: '感知类任务，接入后可见' },
  { value: 'hil', label: 'HIL仿真', color: 'purple', description: 'HIL 仿真任务，在 HIL 仿真 · 创建 Job 中可选' },
  // other：不出现在左栏任一仿真菜单下；用于历史遗留 / 试验型任务，避免占正式坑位。
  { value: 'other', label: '其他', color: 'default', description: '不归属于 PNC / 感知，仅供内部使用' },
];

const simTypeLabel = (v?: SimType | null): string => {
  const hit = SIM_TYPE_OPTIONS.find(o => o.value === v);
  return hit ? hit.label : (v ?? '-');
};
const simTypeColor = (v?: SimType | null): string => {
  const hit = SIM_TYPE_OPTIONS.find(o => o.value === v);
  return hit ? hit.color : 'default';
};

/** 把后端字典转成表单内行数组，default 始终在第 0 行；后端无配置时给一个空 default 占位行 */
function dagMapToEntries(map?: DagTemplateNameMap | null): DagTemplateEntry[] {
  if (!map || Object.keys(map).length === 0) {
    return [{ key: DAG_TEMPLATE_DEFAULT_KEY, value: '' }];
  }
  const entries: DagTemplateEntry[] = [
    { key: DAG_TEMPLATE_DEFAULT_KEY, value: map[DAG_TEMPLATE_DEFAULT_KEY] ?? '' },
  ];
  Object.entries(map).forEach(([k, v]) => {
    if (k !== DAG_TEMPLATE_DEFAULT_KEY) {
      entries.push({ key: k, value: v });
    }
  });
  return entries;
}

/** 把表单行数组转回后端字典，过滤掉空 key/空 value 行；同 key 后写覆盖前 */
function entriesToDagMap(
  entries: DagTemplateEntry[] | undefined,
): DagTemplateNameMap | undefined {
  if (!entries?.length) return undefined;
  const out: DagTemplateNameMap = {};
  entries.forEach(e => {
    const k = (e?.key ?? '').trim();
    const v = (e?.value ?? '').trim();
    if (k && v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
}

/** 字典展示压缩为 ``key=value`` 短串，便于表格列阅读 */
function dagMapToShortDisplay(map?: DagTemplateNameMap | null): string {
  if (!map || Object.keys(map).length === 0) return '-';
  return Object.entries(map).map(([k, v]) => `${k}=${v}`).join('  |  ');
}

const formatTime = (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-');

const EMPTY_EDITOR: EditorState = { open: false, mode: 'create' };

export default function TaskTypeTab() {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const [data, setData] = useState<TaskTypeDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState('');
  // 顶部按归属仿真类型过滤；undefined = 不过滤（显示全部）
  const [simTypeFilter, setSimTypeFilter] = useState<SimType | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);

  const loadList = useCallback(async (
    name?: string,
    simType?: SimType,
  ) => {
    setLoading(true);
    try {
      const rows = await listTaskTypesForAdmin({
        ...(name ? { name } : {}),
        ...(simType ? { simType } : {}),
      });
      setData(rows);
    } catch (e: any) {
      message.error(e?.message || '加载任务类型失败');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadList(searchName || undefined, simTypeFilter);
  }, [loadList, simTypeFilter]);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      visible: true,
      // 新建默认 planning；管理员在感知筛选态下也可切成 perception
      simType: simTypeFilter ?? 'planning',
      dagTemplateEntries: dagMapToEntries(null),
    });
    setEditor({ open: true, mode: 'create' });
  };

  const openEdit = (record: TaskTypeDetail) => {
    form.resetFields();
    form.setFieldsValue({
      name: record.name,
      taskTypeCode: record.taskTypeCode ?? undefined,
      dagTemplateEntries: dagMapToEntries(record.dagTemplateName),
      dispatchProjectId: record.dispatchProjectId ?? undefined,
      visible: (record.enableFlag ?? 1) === 1,
      simType: (record.simType ?? 'planning') as SimType,
      description: record.description ?? undefined,
    });
    setEditor({ open: true, mode: 'edit', record });
  };

  const closeEditor = () => setEditor(EMPTY_EDITOR);

  const submit = async () => {
    try {
      const { visible, dagTemplateEntries, ...rest } = await form.validateFields();
      // 把表单行转回后端字典；为空时不传该字段，与"不修改"语义对齐（update 路径）。
      // create 路径下 default 必填已由 form rule 兜住，不会落到 undefined。
      const dagTemplateName = entriesToDagMap(dagTemplateEntries);
      const payload = {
        ...rest,
        dagTemplateName,
        enableFlag: visible ? 1 : 0,
      };
      setSubmitting(true);
      if (editor.mode === 'create') {
        await createTaskType(payload);
        message.success('新建成功');
      } else if (editor.record) {
        await updateTaskType({ id: editor.record.id, ...payload });
        message.success('更新成功');
      }
      closeEditor();
      await loadList(searchName || undefined, simTypeFilter);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVisible = async (record: TaskTypeDetail, visible: boolean) => {
    try {
      await updateTaskType({ id: record.id, enableFlag: visible ? 1 : 0 });
      message.success(visible ? '已显示' : '已隐藏');
      await loadList(searchName || undefined, simTypeFilter);
    } catch (e: any) {
      message.error(e?.message || '切换状态失败');
    }
  };

  const handleDelete = async (record: TaskTypeDetail) => {
    try {
      await deleteTaskType(record.id);
      message.success('删除成功');
      await loadList(searchName || undefined, simTypeFilter);
    } catch (e: any) {
      message.error(e?.message || '删除失败');
    }
  };

  const columns: ColumnsType<TaskTypeDetail> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '名称', dataIndex: 'name', width: 200, ellipsis: true },
    { title: '编码', dataIndex: 'taskTypeCode', width: 140, render: v => v || '-' },
    {
      title: '归属仿真',
      dataIndex: 'simType',
      width: 110,
      render: (v: SimType | null | undefined) => (
        <Tag color={simTypeColor(v)}>{simTypeLabel(v)}</Tag>
      ),
    },
    {
      title: 'DAG 模板',
      dataIndex: 'dagTemplateName',
      width: 320,
      // showTitle: false 关闭 AntD 默认的原生 title 属性，避免和下面 Tooltip 抢；
      // 同时仍然给单元格加上 overflow:hidden 背景便于 trigger 容器的 max-width:100% 生效。
      ellipsis: { showTitle: false },
      render: (v: DagTemplateNameMap | null | undefined) => (
        <Tooltip
          // 默认 Tooltip 宽度 250px 会把模板名（如 planning_simulation_job_with_build）
          // 截断成 "planning_simul…"，这里取消宽度限制并让 JSON 用等宽字体不换行展示。
          styles={{ root: { maxWidth: 'none' }, body: { maxWidth: 'none' } }}
          placement="topLeft"
          // 锚点元素的 bounding-box = 单元格可见宽度。否则 inline span 会按
          // 自然内容宽度上报，把 Tooltip 推到下一列（"调度项目 ID"）上方。
          title={v
            ? (
              <pre style={{
                margin: 0,
                whiteSpace: 'pre',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: 12,
                color: '#fff',
              }}>
                {JSON.stringify(v, null, 2)}
              </pre>
            )
            : '未配置'}
        >
          <div
            style={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {dagMapToShortDisplay(v)}
          </div>
        </Tooltip>
      ),
    },
    {
      title: '调度项目 ID', dataIndex: 'dispatchProjectId', width: 180, ellipsis: true,
      render: v => v || '-',
    },
    {
      title: '描述', dataIndex: 'description', width: 220, ellipsis: true,
      render: (v: string | null | undefined) => v || '-',
    },
    {
      title: '是否显示', dataIndex: 'enableFlag', width: 120,
      render: (v, record) => (
        <Space>
          {v === 1 ? <Tag color="green">显示</Tag> : <Tag color="default">隐藏</Tag>}
          <Switch
            size="small"
            checked={v === 1}
            onChange={checked => toggleVisible(record, checked)}
          />
        </Space>
      ),
    },
    { title: '创建人', dataIndex: 'creator', width: 160, render: v => v || '-' },
    { title: '创建时间', dataIndex: 'createTime', width: 180, render: formatTime },
    {
      title: '操作', key: 'actions', width: 160, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="删除任务类型"
            description="确认删除该任务类型？被 Job 引用时无法删除，可改为禁用。"
            okText="删除" cancelText="取消" okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          placeholder="按名称搜索"
          prefix={<SearchOutlined />}
          style={{ width: 240 }}
          value={searchName}
          onChange={e => setSearchName(e.target.value)}
          onPressEnter={() => loadList(searchName || undefined, simTypeFilter)}
        />
        <Select
          allowClear
          placeholder="归属仿真：全部"
          style={{ width: 160 }}
          value={simTypeFilter}
          onChange={(v: SimType | undefined) => setSimTypeFilter(v)}
          options={SIM_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
        />
        <Button icon={<SearchOutlined />} onClick={() => loadList(searchName || undefined, simTypeFilter)}>搜索</Button>
        <Button icon={<ReloadOutlined />} onClick={() => { setSearchName(''); setSimTypeFilter(undefined); }}>
          重置
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建任务类型
        </Button>
      </Space>

      <Table<TaskTypeDetail>
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100', '1000'] }}
        scroll={{ x: 1300 }}
        size="middle"
      />

      <Modal
        open={editor.open}
        title={editor.mode === 'create' ? '新建任务类型' : `编辑任务类型 #${editor.record?.id}`}
        onCancel={closeEditor}
        onOk={submit}
        confirmLoading={submitting}
        destroyOnHidden
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" preserve={false} autoComplete="off">
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称', whitespace: true }, { max: 255 }]}
          >
            <Input placeholder="例如：planning_control" />
          </Form.Item>
          <Form.Item name="taskTypeCode" label="编码" rules={[{ max: 64 }]}>
            <Input placeholder="例如：PLC、smart、LPL" />
          </Form.Item>

          <Form.Item
            name="simType"
            label="归属仿真"
            tooltip="决定该任务类型在左栏出现在哪个仿真下。PNC / 感知 会进对应侧栏；其他不进侧栏。"
            rules={[{ required: true, message: '请选择归属仿真' }]}
          >
            <Select
              placeholder="选择归属仿真"
              // 选项自带描述，用 optionRender 展示为「label · description」，便于区分
              options={SIM_TYPE_OPTIONS.map(o => ({
                value: o.value,
                label: o.label,
                description: o.description,
              }))}
              optionRender={(opt) => (
                <Space direction="vertical" size={0} style={{ lineHeight: 1.4 }}>
                  <span>{opt.data.label}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {opt.data.description}
                  </Typography.Text>
                </Space>
              )}
            />
          </Form.Item>

          <Form.Item
            label={(
              <Space size={4}>
                <span>DAG 模板</span>
                <Tooltip
                  styles={{ root: { maxWidth: 420 } }}
                  title={(
                    <div style={{ lineHeight: 1.6 }}>
                      <div>
                        按 build_type 多路映射 DAG 模板。dispatch 时按当前 Job 的
                        buildType 命中对应 value，命中失败走 default 兜底。
                      </div>
                      <div style={{ marginTop: 8 }}>
                        已知 build_type：
                        {KNOWN_BUILD_TYPES.map(t => (
                          <code
                            key={t}
                            style={{
                              marginInlineStart: 6,
                              padding: '0 4px',
                              borderRadius: 3,
                              background: 'rgba(255,255,255,0.15)',
                            }}
                          >
                            {t}
                          </code>
                        ))}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        default 必填，其他 key/value 可选。
                      </div>
                    </div>
                  )}
                >
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            )}
            required
          >
            <Form.List
              name="dagTemplateEntries"
              rules={[{
                validator: async (_, entries: DagTemplateEntry[]) => {
                  const list = Array.isArray(entries) ? entries : [];
                  const defaultEntry = list.find(
                    e => (e?.key ?? '').trim() === DAG_TEMPLATE_DEFAULT_KEY,
                  );
                  if (!defaultEntry || !(defaultEntry.value ?? '').trim()) {
                    throw new Error('default 模板必填');
                  }
                  // 重复 key 校验：除 default 外（默认行 key 锁死，不会重复输入），
                  // 用户自加的行可能重复，提示一下避免后写覆盖前写。
                  const seen = new Set<string>();
                  for (const e of list) {
                    const k = (e?.key ?? '').trim();
                    if (!k) continue;
                    if (seen.has(k)) {
                      throw new Error(`存在重复的 key: ${k}`);
                    }
                    seen.add(k);
                  }
                },
              }]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map((field, idx) => {
                    const isDefault = idx === 0;
                    const keyRules: Rule[] = isDefault
                      ? []
                      : [
                        { required: true, message: '请输入 key', whitespace: true },
                        { max: 64, message: 'key 长度不超过 64' },
                      ];
                    const valueRules: Rule[] = [
                      { required: true, message: '请输入模板名', whitespace: true },
                      { max: 128, message: '模板名长度不超过 128' },
                    ];
                    return (
                      <Space.Compact
                        key={field.key}
                        style={{ display: 'flex', marginBottom: 8 }}
                      >
                        <Form.Item
                          {...field}
                          name={[field.name, 'key']}
                          rules={keyRules}
                          noStyle
                        >
                          {isDefault ? (
                            <Input
                              disabled
                              style={{ width: 200 }}
                              value={DAG_TEMPLATE_DEFAULT_KEY}
                            />
                          ) : (
                            <AutoComplete
                              style={{ width: 200 }}
                              placeholder="build_type，如 compile_platform"
                              options={KNOWN_BUILD_TYPES.map(t => ({ value: t }))}
                              filterOption={(input, opt) =>
                                (opt?.value as string)
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                            />
                          )}
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'value']}
                          rules={valueRules}
                          noStyle
                        >
                          <Input
                            placeholder={
                              isDefault
                                ? '默认 DAG 模板名（兜底必填）'
                                : '该 build_type 对应的 DAG 模板名'
                            }
                          />
                        </Form.Item>
                        {!isDefault && (
                          <Button
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </Space.Compact>
                    );
                  })}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ key: '', value: '' })}
                    block
                  >
                    新增 build_type 模板映射
                  </Button>
                  <Form.ErrorList errors={errors} />
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', marginTop: 4, fontSize: 12 }}
                  >
                    第一行 default 为兜底必填；其余行 key 与后端 BuildType 对齐
                    （目前已知 {KNOWN_BUILD_TYPES.join(' / ')}）。
                  </Typography.Text>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item
            name="dispatchProjectId"
            label="调度项目 ID"
            rules={[{ max: 64 }]}
            tooltip="compute-flow 调度平台的 project_id"
          >
            <Input placeholder="例如：proj-xxx" />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
            tooltip="运营给该任务类型的说明,展示在后台管理表格"
          >
            <Input.TextArea rows={2} placeholder="可选" maxLength={500} showCount />
          </Form.Item>
          <Form.Item
            name="visible"
            label="是否显示"
            valuePropName="checked"
            tooltip="关闭后，该任务类型不会出现在前台任务类型下拉中"
          >
            <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
